import { getNextEvent } from "core-integration/src/store/events/reducers";
import {
  getLoginSuccess,
  getPlayerData,
} from "core-integration/src/store/player/reducers";
import { IPlayer } from "core-integration/src/store/player/types";
import React, { lazy, Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  Outlet,
  Route,
  Routes as RouterRoutes,
  useNavigate,
} from "react-router";
import { createCustomInteractionData } from "../utils/tealium";
import { Loading } from "./App";
import Copy from "./Copy";
import EntryHistory from "./EntryHistory";
import EntryUnsubscribe from "./EntryUnsubscribe";
import EntryUpdate from "./EntryUpdate";
import FixturesWrapper from "./FixturesWrapper";
import PlayerList from "./help/PlayerList";
import Home from "./Home";
import { Main, Wrapper } from "./Layout";
import AdminClassic from "./leagues/AdminClassic";
import AutoJoin from "./leagues/AutoJoin";
import CreateClassic from "./leagues/CreateClassic";
import CreateJoin from "./leagues/CreateJoin";
import Invite from "./leagues/Invite";
import Join from "./leagues/Join";
import JoinPrivate from "./leagues/JoinPrivate";
import JoinPublic from "./leagues/JoinPublic";
import MyLeagues from "./leagues/MyLeagues";
import Renew from "./leagues/Renew";
import StandingsClassic from "./leagues/StandingsClassic";
import Login from "./Login";
import PlayerNews from "./news/playerNews";
import Logout from "./player/Logout";
import Register from "./player/Register";
import PostSeason from "./PostSeason";
import Prizes from "./prizes/Prizes";
import SquadSelection from "./squad/SquadSelection";
import Transfers from "./squad/Transfers";
import Statistics from "./stats/Statistics";
import DreamTeam from "./team/DreamTeam";
import EntryEvent from "./team/EntryEvent";
import MyTeam from "./team/MyTeam";
import TransfersHistory from "./TransfersHistory";

const Help = lazy(() => import("./help/Help"));
const Rules = lazy(() => import("./help/Rules"));
const Terms = lazy(() => import("./help/Terms"));
const Updates = lazy(() => import("./help/Updates"));

interface IPropsFromState {
  player: IPlayer | null;
}

const PlayerRoute = () => {
  const player = useSelector(getPlayerData);
  if (player && !player.entry) {
    return <Outlet />;
  } else if (player && player.dirty) {
    return <Register />;
  } else {
    return (
      <Wrapper>
        <Main isWide>
          <Login />
        </Main>
      </Wrapper>
    );
  }
};

const EntryRoute = () => {
  const player = useSelector(getPlayerData);
  if (player && player.entry && !player.dirty) {
    return <Outlet />;
  } else if (player && player.dirty) {
    return <Register />;
  } else {
    return (
      <Wrapper>
        <Main isWide>
          <Login />
        </Main>
      </Wrapper>
    );
  }
};

export const NotFound = () => {
  const { t } = useTranslation();
  return (
    <Wrapper>
      <Main>
        <Copy>
          <h4>{t("routes.notFound.title", "Page not found")}</h4>
          <p>
            {t(
              "routes.notFound.text",
              "Sorry, but the page you were looking for can't be found.",
            )}
          </p>
        </Copy>
      </Main>
    </Wrapper>
  );
};

const Routes: React.FC<IPropsFromState> = () => {
  const player = useSelector(getPlayerData);
  const loginSuccess = useSelector(getLoginSuccess);
  const [hasPlayer, setHasPlayer] = useState(false);
  const [hasLoginSuccess, setHasLoginSuccess] = useState(false);
  const nextEvent = useSelector(getNextEvent);
  const navigate = useNavigate();

  useEffect(() => {
    if (player) {
      setHasPlayer(true);
    }

    if (loginSuccess) {
      setHasLoginSuccess(true);
    }

    if (hasPlayer && hasLoginSuccess) {
      const userData = {
        userState: "authenticated",
        userId: player?.nbaciamguid,
      };
      createCustomInteractionData({
        contentTitle: "home",
        interactionId: "fantasy:sign-in:success",
        userData: userData,
        ampEventName: "User Account: Login Success",
      });
      if (player?.id && !player?.entry) {
        navigate("/squad-selection");
      }
      setHasLoginSuccess(false);
    }
  }, [loginSuccess, hasLoginSuccess, player, hasPlayer]);

  return (
    <Suspense fallback={<Loading />}>
      <RouterRoutes>
        <Route path="/" element={<Home />} />
        {player ? (
          nextEvent ? (
            <Route element={<PlayerRoute />}>
              <Route path="/squad-selection" element={<SquadSelection />} />
            </Route>
          ) : (
            <Route element={<PlayerRoute />}>
              <Route element={<PostSeason />} path="/update" />
            </Route>
          )
        ) : null}
        {player?.entry && nextEvent ? (
          <Route element={<EntryRoute />}>
            <Route path="/my-team" element={<MyTeam />} />
          </Route>
        ) : (
          <Route element={<PlayerRoute />}>
            <Route path="/update" element={<PostSeason />} />
          </Route>
        )}
        <Route element={<EntryRoute />}>
          <Route path="/transfers" element={<Transfers />} />
          <Route path="/leagues" element={<MyLeagues />} />
          <Route path="/leagues/renew" element={<Renew />} />
          <Route path="/leagues/create-join" element={<CreateJoin />} />
          <Route path="/leagues/create/classic" element={<CreateClassic />} />
          <Route path="/leagues/join" element={<Join />} />
          <Route path="/leagues/join/private" element={<JoinPrivate />} />
          <Route path="/leagues/join/public" element={<JoinPublic />} />
          <Route path="/leagues/:leagueId/admin/c" element={<AdminClassic />} />
          <Route path="/leagues/:leagueId/invite" element={<Invite />} />
        </Route>

        <Route
          path="/leagues/:leagueId/standings/c"
          element={<StandingsClassic />}
        />
        <Route path="/entry-update" element={<EntryUpdate />} />
        <Route path="/entry/:entryId/history" element={<EntryHistory />} />
        <Route path="/entry/:entryId/event/:eventId" element={<EntryEvent />} />
        <Route
          path="/entry/:entryId/transfers"
          element={<TransfersHistory />}
        />
        <Route path="/dream-team/" element={<DreamTeam />} />
        <Route path="/dream-team/:eventId" element={<DreamTeam />} />
        <Route path="/statistics" element={<Statistics />} />
        <Route path="/statistics/:statName" element={<Statistics />} />
        <Route path="/fixtures" element={<FixturesWrapper />} />
        <Route path="/fixtures/:eventId" element={<FixturesWrapper />} />
        <Route path="/player-news" element={<PlayerNews />} />
        <Route path="/help" element={<Help />} />
        <Route path="/help/rules" element={<Rules />} />
        <Route path="/help/terms" element={<Terms />} />
        <Route path="/help/updates" element={<Updates />} />
        <Route path="/prizes" element={<Prizes />} />
        <Route path="/player-list" element={<PlayerList />} />
        <Route path="/leagues/auto-join/:code" element={<AutoJoin />} />
        <Route path="/player/logout" element={<Logout />} />
        <Route
          path="/entry/unsubscribe/:signature"
          element={<EntryUnsubscribe />}
        />
        <Route path="*" element={<NotFound />} />
        <Route path="/player" element={<Register />} />
      </RouterRoutes>
    </Suspense>
  );
};

export default Routes;
